import {
  ApolloCache,
  FetchResult,
  MaybeMasked,
  useApolloClient,
  useFragment
} from "@apollo/client"
import { useControllableState } from "@chakra-ui/react"
import {
  UsePaginatorReturn,
  useLocalStorage,
  usePaginator
} from "@pathwright/utils"
import { LocalStorageValue } from "@pathwright/utils/src/storage/useLocalStorage"
import produce from "immer"
import isEmpty from "lodash/isEmpty"
import throttle from "lodash/throttle"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createContext, useContextSelector } from "use-context-selector"
import {
  CreateDiscussionMutationVariables,
  CreateDiscussionResponseLikeMutationResult,
  CreateDiscussionResponseMutationVariables,
  CreateDiscussionSubscriptionMutationResult,
  DeleteDiscussionResponseLikeMutationResult,
  DeleteDiscussionResponseMutationResult,
  DeleteDiscussionSubscriptionMutationResult,
  DiscussionContextInput,
  DiscussionDocument,
  DiscussionPostFragment,
  DiscussionPostFragmentDoc,
  DiscussionQuery,
  DiscussionQueryVariables,
  DiscussionResponseFragment,
  ResponseOrderBy,
  ResponsesDocument,
  ResponsesQuery,
  ResponsesQueryVariables,
  UpdateDiscussionResponseMutationResult,
  UpdateUserDiscussionHistoryMutationResult,
  useCreateDiscussionMutation,
  useCreateDiscussionResponseLikeMutation,
  useCreateDiscussionResponseMutation,
  useCreateDiscussionSubscriptionMutation,
  useDeleteDiscussionResponseLikeMutation,
  useDeleteDiscussionResponseMutation,
  useDeleteDiscussionSubscriptionMutation,
  useDiscussionQuery,
  useResponsesQuery,
  useUpdateDiscussionResponseMutation,
  useUpdateUserDiscussionHistoryMutation
} from "@pathwright/graphql-client/src/generated"
import { useNotificationsState } from "../notifications/state/notification-state"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { canAdministrate } from "../user/permissions"
import { orderResponsesBySchema } from "./panel/DiscussionResponsesOrderBySelect"
import useRefetchSyncPlanForDiscussion from "./useRefetchSyncPlanForDiscussion"

export type NonArchivedDiscussionResponseFragment =
  DiscussionResponseFragment & {
    // We're not dealing with archived responses here, so user is always defined.
    user: NonNullable<DiscussionResponseFragment["user"]>
  }

// Get the cached discussion post when possible rather than querying.
function useCachedDiscussionPost(discussionId?: number | null) {
  if (!discussionId) {
    return null
  }

  try {
    const discussionFragment = useFragment({
      fragment: DiscussionPostFragmentDoc,
      fragmentName: "DiscussionPost",
      from: {
        __typename: "Discussion",
        id: discussionId
      }
    })

    // Note: we could use the partial fragment but we'd have to be careful.
    // This is where TS would be helpful.
    return discussionFragment?.complete ? discussionFragment?.data : null
  } catch {
    return null
  }
}

type DiscussionContextType = {
  context: DiscussionContextInput
  loading: boolean
  id: number | null
  responseId: number | null
  post: DiscussionPostFragment | null
  postLoading: boolean
  postError?: Error
  response: DiscussionResponseFragment | null
  responseDeleted: boolean
  responses: DiscussionResponseFragment[]
  responsesLoading: boolean
  responsesError: Error | undefined
  responsesPaginator: UsePaginatorReturn
  replyingTo: NonArchivedDiscussionResponseFragment | null
  editingResponse: NonArchivedDiscussionResponseFragment | null
  deletingResponse: NonArchivedDiscussionResponseFragment | null
  setUnreadResponses: (responses: DiscussionResponseFragment[]) => void
  unreadResponses: DiscussionResponseFragment[]
  markAllResponsesAsRead: () => void
  setReplyingTo: (
    response: NonArchivedDiscussionResponseFragment | null
  ) => void
  setEditingResponse: (
    response: NonArchivedDiscussionResponseFragment | null
  ) => void
  setDeletingResponse: (
    response: NonArchivedDiscussionResponseFragment | null
  ) => void
  scrollToResponse: (response: NonArchivedDiscussionResponseFragment) => void
  scrollRef: React.RefObject<any>
  // Whether to visually reverse the order of responses in the UI.
  visuallyReverseResponses?: boolean
  viewResponse: (response: NonArchivedDiscussionResponseFragment) => void
  viewAllResponses: () => void
  orderResponsesBy: ResponseOrderBy
  setOrderResponsesBy: (orderBy: LocalStorageValue<ResponseOrderBy>) => void
  createDiscussion: (
    args: Pick<CreateDiscussionMutationVariables, "initialResponse"> &
      CreateDiscussionMutationVariables["post"]
  ) => Promise<void>
  updateUserDiscussionHistory: () =>
    | Promise<
        FetchResult<
          MaybeMasked<UpdateUserDiscussionHistoryMutationResult["data"]>
        >
      >
    | undefined
  subscribeToDiscussion: () => Promise<
    FetchResult<MaybeMasked<CreateDiscussionSubscriptionMutationResult["data"]>>
  >
  unsubscribeToDiscussion: () => Promise<
    FetchResult<MaybeMasked<DeleteDiscussionSubscriptionMutationResult["data"]>>
  >
  createResponse: (
    response: CreateDiscussionResponseMutationVariables["response"]
  ) => Promise<any>
  updateResponse: (
    response: NonArchivedDiscussionResponseFragment
  ) => Promise<
    FetchResult<MaybeMasked<UpdateDiscussionResponseMutationResult["data"]>>
  >
  deleteResponse: (
    response: NonArchivedDiscussionResponseFragment
  ) => Promise<
    FetchResult<MaybeMasked<DeleteDiscussionResponseMutationResult["data"]>>
  >
  likeResponse: (
    response: NonArchivedDiscussionResponseFragment
  ) => Promise<
    FetchResult<MaybeMasked<CreateDiscussionResponseLikeMutationResult["data"]>>
  >
  unlikeResponse: (
    response: NonArchivedDiscussionResponseFragment
  ) => Promise<
    FetchResult<MaybeMasked<DeleteDiscussionResponseLikeMutationResult["data"]>>
  >
}

const DiscussionContext = createContext<DiscussionContextType>(
  {} as DiscussionContextType
)

export function useDiscussionContext<T = DiscussionContextType>(
  selector?: (value: DiscussionContextType) => T
): T {
  if (selector) {
    return useContextSelector(DiscussionContext, selector)
  }
  return useContextSelector(DiscussionContext, (v) => v) as T
}

type DiscussionResponseEdge = NonNullable<
  NonNullable<ResponsesQuery["responses"]>["edges"]
>[number]

export type DiscussionProviderProps = {
  context: DiscussionContextInput
  id?: number | null
  responseId?: number | null
  visuallyReverseResponses?: boolean
  includeArchivedResponses?: boolean
  orderResponsesBy?: ResponseOrderBy
  onViewResponse?: (response: DiscussionResponseFragment) => void
  onViewAllResponses?: () => void
  onDiscussionCreated?: (post: DiscussionPostFragment) => void
  onDiscussionResponseCreated?: (
    post: DiscussionPostFragment,
    response: DiscussionResponseFragment,
    responseParent?: DiscussionResponseFragment | null
  ) => void
  onDiscussionResponseDeleted?: (post: DiscussionPostFragment) => void
  children: React.ReactNode
}

function DiscussionProvider({
  id,
  context: contextProp,
  responseId,
  includeArchivedResponses,
  visuallyReverseResponses,
  orderResponsesBy: orderResponsesByProp,
  onViewResponse,
  onViewAllResponses,
  onDiscussionCreated,
  onDiscussionResponseCreated,
  onDiscussionResponseDeleted,
  children
}: DiscussionProviderProps) {
  const pwContext = usePathwrightContext()
  const { me } = pwContext

  // Memoize context to avoid unnecessary re-renders.
  const context = useMemo(() => {
    return contextProp
  }, [
    contextProp.cohort_id,
    contextProp.resource_id,
    contextProp.step_id,
    contextProp.completion_id
  ])

  const refetchSyncPlanForDiscussion = useRefetchSyncPlanForDiscussion()
  // Using stored orderResponsesBy to persist user's preference, but only when
  // not controlled via prop.
  const [storedOrderResponsesBy, setStoredOrderResponsesBy] = useLocalStorage(
    "orderResponsesBy",
    ResponseOrderBy.CreationDesc
  )
  const [orderResponsesBy] = useControllableState({
    value: orderResponsesByProp || storedOrderResponsesBy,
    defaultValue: ResponseOrderBy.CreationDesc
  })

  // If the stored order responses by is not valid, set it to the default.
  useEffect(() => {
    if (
      storedOrderResponsesBy &&
      !orderResponsesBySchema.safeParse(storedOrderResponsesBy).success
    ) {
      console.log("setting stored order responses by to", orderResponsesBy)
      setStoredOrderResponsesBy(ResponseOrderBy.CreationDesc)
    }
  }, [storedOrderResponsesBy])

  // We'll hook into the user's notifications to refetch responses when user
  // is notified of a new response (and to possibly mark responses as read when viewed).
  const { items: notifications } = useNotificationsState()

  const cachedDiscussion = useCachedDiscussionPost(id)

  const discussionQueryVariables: DiscussionQueryVariables = useMemo(
    () => ({ id, context }),
    [id, context]
  )
  const responsesQueryVariables: ResponsesQueryVariables = useMemo(
    () => ({
      context: {
        ...context,
        discussion_id: id
      },
      rootResponseId: responseId ? { eq: responseId } : null,
      archivedTime: includeArchivedResponses ? null : { eq: null },
      orderBy: orderResponsesBy,
      first: 50
    }),
    [context, id, responseId, includeArchivedResponses, orderResponsesBy]
  )

  // Writes through the mutation `cache` argument so they join Apollo's
  // optimistic overlay and roll back on error. ObservableQuery.updateQuery
  // writes the root store and survives removeOptimistic().
  const writeDiscussionQuery = useCallback(
    (cache: ApolloCache<unknown>, updater: (draft: DiscussionQuery) => void) => {
      cache.updateQuery<DiscussionQuery, DiscussionQueryVariables>(
        { query: DiscussionDocument, variables: discussionQueryVariables },
        (previousResult) => {
          if (!previousResult) return previousResult
          return produce(previousResult, updater)
        }
      )
    },
    [discussionQueryVariables]
  )
  const writeResponsesQuery = useCallback(
    (cache: ApolloCache<unknown>, updater: (draft: ResponsesQuery) => void) => {
      cache.updateQuery<ResponsesQuery, ResponsesQueryVariables>(
        { query: ResponsesDocument, variables: responsesQueryVariables },
        (previousResult) => {
          if (!previousResult) return previousResult
          return produce(previousResult, updater)
        }
      )
    },
    [responsesQueryVariables]
  )

  const discussionQuery = useDiscussionQuery({
    variables: discussionQueryVariables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: !!cachedDiscussion
  })
  const {
    data: discussionData,
    previousData: prevDiscussionData,
    ...discussionState
  } = discussionQuery

  const responsesQuery = useResponsesQuery({
    variables: responsesQueryVariables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true
  })
  const {
    data: responsesData,
    previousData: prevResponsesData,
    ...responsesState
  } = responsesQuery

  // Only use previousData when the current result is missing (loading/error).
  // After optimistic rollback, data is `{ discussion: null }` and previousData
  // still holds the temp post — that must not keep filling `post`.
  const post =
    cachedDiscussion ||
    discussionData?.discussion ||
    (discussionData == null ? prevDiscussionData?.discussion : undefined) ||
    null

  const postId = post?.id

  const responsesPaginator = usePaginator({
    query: responsesQuery,
    paginatorValue: responsesQuery.data?.responses
  })

  useEffect(() => {
    // Attempt to find an unread response notification for this discussion.
    const unreadNotification = notifications.find((n) => {
      if (n.data.context_data) {
        const { discussion, response } = n.data.context_data
        // Must be for this discussion and be recent (after last viewed time).
        const isForDiscussion = response && parseInt(discussion) === post?.id
        const isRecent =
          n.data.when > new Date(post?.user_last_viewed_time || 0).getTime()

        return isForDiscussion && isRecent
      }
    })

    // If found, refetch reponses (or we may wish to show a toast to the user,
    // prompting them to view the new response). In some cases, the responses list
    // may be long, and we can't be sure the new response will appear in the current page,
    // so linking directly to the response in a toast would be better.
    if (unreadNotification) {
      discussionQuery.refetch() // for accurate total_responses
      responsesQuery.refetch() // for new responses
      // Update last viewed time for discussion? Otherwise the unread indicator will
      // appear until user manually views the discussion.
      updateUserDiscussionHistory()
    }
  }, [notifications])

  const [updateUserDiscussionHistoryMutation] =
    useUpdateUserDiscussionHistoryMutation()
  const apolloClient = useApolloClient()

  // Cache-only: use on optimistic passes. Network mutation belongs on success.
  const writeDiscussionViewedToCache = useCallback(
    (
      cache: ApolloCache<unknown>,
      discussion: Pick<DiscussionPostFragment, "id">,
      options?: { asResponded?: boolean }
    ) => {
      if (!discussion?.id || discussion.id <= 0) return
      const now = new Date().toISOString()
      cache.modify({
        id: cache.identify({
          __typename: "Discussion",
          id: discussion.id
        }),
        fields: {
          user_last_viewed_time: () => now,
          user_has_viewed_recent: () => true,
          ...(options?.asResponded
            ? {
                last_activity_time: () => now,
                user_last_responded_time: () => now
              }
            : null)
        }
      })
    },
    []
  )

  // Network write after any optimistic cache update. Always reinforces the
  // viewed fields in the root cache first so a concurrent Discussion refetch
  // (or optimistic rollback) can't leave the unread indicator flashing while
  // the request is in flight.
  const markDiscussionViewed = useCallback(
    (
      discussion: DiscussionPostFragment | null | undefined,
      options?: { asResponded?: boolean }
    ) => {
      if (!discussion || discussion.id <= 0) return
      writeDiscussionViewedToCache(apolloClient.cache, discussion, options)
      return updateUserDiscussionHistoryMutation({
        variables: { context, discussion_id: discussion.id }
      })
    },
    [
      context,
      updateUserDiscussionHistoryMutation,
      apolloClient.cache,
      writeDiscussionViewedToCache
    ]
  )

  const [createDiscussionMutation, createDiscussionState] =
    useCreateDiscussionMutation({
      onCompleted: ({ createDiscussion }) => {
        onDiscussionCreated?.(createDiscussion!)
        refetchSyncPlanForDiscussion(createDiscussion!)
      }
    })
  const [createResponseMutation] = useCreateDiscussionResponseMutation()
  const [updateResponseMutation] = useUpdateDiscussionResponseMutation()
  const [deleteResponseMutation] = useDeleteDiscussionResponseMutation({
    onCompleted: () => onDiscussionResponseDeleted?.(post!)
  })
  const [createResponseLikeMutation] = useCreateDiscussionResponseLikeMutation()
  const [deleteResponseLikeMutation] = useDeleteDiscussionResponseLikeMutation()
  const [subscribeToDiscussionMutation] =
    useCreateDiscussionSubscriptionMutation()
  const [unsubscribeToDiscussionMutation] =
    useDeleteDiscussionSubscriptionMutation()

  const [replyingTo, setReplyingTo] =
    useState<NonArchivedDiscussionResponseFragment | null>(null)
  const [editingResponse, setEditingResponse] =
    useState<NonArchivedDiscussionResponseFragment | null>(null)
  const [deletingResponse, setDeletingResponse] =
    useState<NonArchivedDiscussionResponseFragment | null>(null)
  const [response, setResponse] = useState<DiscussionResponseFragment | null>(
    null
  )
  const [unreadResponses, setUnreadResponses] = useState<
    DiscussionResponseFragment[]
  >([])

  const scrollRef = useRef<any>()

  const responsesEdges =
    responsesData?.responses ||
    (responsesData == null ? prevResponsesData?.responses : undefined)
  const responses = useMemo(
    () => responsesEdges?.edges?.map((edge) => edge!.node!) ?? [],
    [responsesEdges]
  )

  // Preserving legacy behavior of hiding archived responses without unarchived children.
  // This could be done (and likely should be done) on the server, but doing client-side for now.
  // Attempted server-side filtering in a refactor/react-discussion-archived-filter-attempt branch,
  // but the query times out.
  const isAdmin = canAdministrate(pwContext)
  const filteredResponses = useMemo(() => {
    // Inbox and other views that omit archived responses don't have an
    // "archived" row UI, so never keep deleted responses in that list —
    // including for admins, who otherwise bypass the filter below.
    if (!includeArchivedResponses) {
      return responses.filter((response) => !response.archived_time)
    }

    // Admin can see all archived responses.
    if (isAdmin) {
      return responses
    }

    // Map of response tree IDs we've already checked with a boolean indicating
    // if they are unarchived or if they have unarchived children.
    const showResponseIds = new Set<number>()

    function buildIdsTree(responses: DiscussionResponseFragment[]) {
      const [response, ...rest] = responses
      if (!response) {
        return
      }

      // When response is not archived, show it.
      if (!response.archived_time) {
        showResponseIds.add(response.id)
      }

      // When a response is marked to be shown, ensure its parent is also shown.
      if (showResponseIds.has(response.id) && response.parent_id) {
        showResponseIds.add(response.parent_id)
      }

      return buildIdsTree(rest)
    }

    buildIdsTree(responses.slice().reverse())

    return responses.filter((response) => showResponseIds.has(response.id))
  }, [responses, includeArchivedResponses, isAdmin])

  useEffect(
    () =>
      setResponse(
        responseId && filteredResponses.length ? filteredResponses[0] : null
      ),
    [filteredResponses, responseId]
  )

  const scrollToResponse = useCallback(
    (r: DiscussionResponseFragment) => {
      const index = responses.findIndex((x) => x.id === r.id)
      if (scrollRef.current) {
        scrollRef.current.scrollToIndex({
          index: index !== -1 ? index : 0,
          viewOffset: 0,
          viewPosition: 0.3
        })
      }
    },
    [responses]
  )

  useEffect(() => {
    if (replyingTo) scrollToResponse(replyingTo)
    if (editingResponse) scrollToResponse(editingResponse)
  }, [replyingTo, editingResponse, scrollToResponse])

  useEffect(() => {
    // Mark viewed whenever the discussion is open — including empty threads —
    // so the first response doesn't race last_activity ahead of last_viewed.
    if (post && !isEmpty(post) && post.id > 0) {
      updateUserDiscussionHistory()
    }
  }, [!!post])

  const markAllResponsesAsRead = useCallback(
    throttle(() => {
      setTimeout(() => setUnreadResponses([]), 1000)
    }, 1000),
    []
  )

  const viewResponse = useCallback(
    (r: DiscussionResponseFragment) => {
      setResponse(r)
      onViewResponse?.(r)
    },
    [onViewResponse]
  )

  const viewAllResponses = useCallback(() => {
    setResponse(null)
    onViewAllResponses?.()
  }, [onViewAllResponses])

  // Mutations wiring
  const createDiscussion = useCallback<
    DiscussionContextType["createDiscussion"]
  >(
    async ({ title, body, type, initialResponse }) => {
      const optimisticResponseId = -Math.floor(Math.random() * 1000000)
      const newPost: DiscussionPostFragment = {
        id: optimisticResponseId,
        title: title!,
        body: body!,
        type: type!,
        likes: 0,
        created_time: new Date().toISOString(),
        is_discussion_question: false,
        allow_replies: true,
        total_responses: initialResponse ? 1 : 0,
        last_activity_time: new Date().toISOString(),
        user_last_responded_time: new Date().toISOString(),
        user_last_viewed_time: new Date().toISOString(),
        user_has_viewed_recent: true,
        // Auto-subscribed to feedback discussions.
        user_is_subscribed: type === 25,
        user_liked: false,
        context_id: -Math.floor(Math.random() * 1000000),
        resource_id: context.resource_id || null,
        cohort_id: context.cohort_id || null,
        step_id: context.step_id || context.completion_id || null,
        user: me,
        cohort: null,
        resource: null,
        step: null,
        tagLinks: null,
        __typename: "Discussion"
      }

      await createDiscussionMutation({
        variables: {
          context,
          post: { title, body, type },
          initialResponse
        },
        refetchQueries: ["Discussion", "Responses"],
        optimisticResponse: {
          createDiscussion: newPost
        },
        update: (cache, { data }) => {
          // Always write the mutation result (optimistic or real). Writing
          // `newPost` on the real pass left a negative discussion id in cache
          // and caused follow-up createResponse calls to fail/roll back.
          const createdDiscussion = data?.createDiscussion
          if (!createdDiscussion) return

          writeDiscussionQuery(cache, (draft) => {
            draft.discussion = createdDiscussion
          })

          // Seed the responses list when it's still empty. Runs on both the
          // optimistic and real passes so the initial response doesn't flash
          // away between optimistic clear and refetchQueries. Once total is
          // already 1 (optimistic seed or refetch), leave the list alone.
          if (initialResponse) {
            writeResponsesQuery(cache, (draft) => {
              if (!draft.responses || (draft.responses.total || 0) !== 0) {
                return
              }
              draft.responses.total = 1
              draft.responses.edges.unshift({
                node: {
                  id: -Math.floor(Math.random() * 1000000),
                  parent_id: null,
                  discussion_id: createdDiscussion.id,
                  body: initialResponse?.body!,
                  attachments: initialResponse?.attachments || [],
                  depth: initialResponse?.depth || 1,
                  likes: 0,
                  user_liked: false,
                  created_time: new Date().toISOString(),
                  archived_time: null,
                  user: me,
                  __typename: "Response"
                },
                __typename: "ResponseEdge"
              })
            })
          }
        }
      }).then((result) => {
        // refetchQueries can land with last_activity ahead of last_viewed
        // after creating a discussion + initial response.
        markDiscussionViewed(result.data?.createDiscussion)
      })
    },
    [
      context,
      createDiscussionMutation,
      writeDiscussionQuery,
      writeResponsesQuery,
      me,
      markDiscussionViewed
    ]
  )

  const updateResponse = useCallback(
    (response: DiscussionResponseFragment) =>
      updateResponseMutation({
        variables: {
          context,
          response_id: response.id,
          response: {
            body: response.body,
            attachments: response.attachments,
            discussion_id: response.discussion_id
          }
        },
        optimisticResponse: {
          updateDiscussionResponse: { ...response, __typename: "Response" }
        }
      }),
    [context, updateResponseMutation]
  )

  const deleteResponse = useCallback(
    (response: DiscussionResponseFragment) =>
      deleteResponseMutation({
        variables: {
          response_id: response.id,
          discussion_id: response.discussion_id,
          context
        },
        optimisticResponse: {
          deleteDiscussionResponse: {
            ...response,
            archived_time: new Date().toISOString(),
            __typename: "Response"
          }
        },
        update: (cache) => {
          const responseCacheId = cache.identify({
            __typename: "Response",
            id: response.id
          })

          // Always decrement total responses count since the total_responses field counts non-archived responses.
          cache.modify({
            id: cache.identify({
              __typename: "Discussion",
              id: response.discussion_id
            }),
            fields: {
              total_responses(existing = 0) {
                return Math.max(0, existing - 1)
              }
            }
          })

          // Persist archived_time on the entity so archived UIs update even if
          // the mutation payload is sparse. Do this before any list rewrite.
          if (responseCacheId) {
            cache.modify({
              id: responseCacheId,
              fields: {
                archived_time: () => new Date().toISOString()
              }
            })
          }

          // Manually update cache to remove deleted response when not including archived responses.
          if (!includeArchivedResponses) {
            // Don't evict+gc here. Create already learned that gc during the
            // mutation update can drop the active Responses query; the hook then
            // fills from previousData and the deleted row comes back. Filtering
            // the query result is enough for views that omit archived responses.
            writeResponsesQuery(cache, (draft) => {
              // Decrement discussion responses count and remove the deleted response from the list.
              if (draft.responses) {
                draft.responses.total = Math.max(
                  0,
                  (draft.responses.total || 0) - 1
                )
                draft.responses.edges = (draft.responses.edges || []).filter(
                  // Note sure why, but the update() called after mutation succeeds results in empty object node
                  // so checking presence of id to exclude that.
                  (edge) => edge?.node?.id && edge.node.id !== response.id
                )
              }
            })
          }
        }
      }),
    [
      context,
      includeArchivedResponses,
      deleteResponseMutation,
      writeResponsesQuery
    ]
  )

  const likeResponse = useCallback(
    (response: DiscussionResponseFragment) =>
      createResponseLikeMutation({
        variables: {
          response_id: response.id,
          discussion_id: response.discussion_id,
          context
        },
        optimisticResponse: {
          __typename: "Mutations",
          createDiscussionResponseLike: {
            ...response,
            user_liked: true,
            likes: (response.likes || 0) + 1,
            __typename: "Response"
          }
        }
      }),
    [context, createResponseLikeMutation]
  )

  const unlikeResponse = useCallback(
    (response: DiscussionResponseFragment) =>
      deleteResponseLikeMutation({
        variables: {
          response_id: response.id,
          discussion_id: response.discussion_id,
          context
        },
        optimisticResponse: {
          __typename: "Mutations",
          deleteDiscussionResponseLike: {
            ...response,
            user_liked: false,
            likes: (response.likes || 0) - 1,
            __typename: "Response"
          }
        }
      }),
    [context, deleteResponseLikeMutation]
  )

  const updateUserDiscussionHistory = useCallback(() => {
    // We don't want to update user discussion history for temporary optimistic posts.
    return markDiscussionViewed(post)
  }, [post, markDiscussionViewed])

  const subscribeToDiscussion = useCallback(
    () =>
      subscribeToDiscussionMutation({
        variables: { context, discussion_id: postId },
        optimisticResponse: {
          createDiscussionSubscription: {
            ...post,
            user_is_subscribed: true,
            __typename: "Discussion"
          }
        }
      }),
    [context, postId, subscribeToDiscussionMutation]
  )

  const unsubscribeToDiscussion = useCallback(
    () =>
      unsubscribeToDiscussionMutation({
        variables: { context, discussion_id: postId },
        optimisticResponse: {
          deleteDiscussionSubscription: {
            ...post,
            user_is_subscribed: false,
            __typename: "Discussion"
          }
        }
      }),
    [context, postId, unsubscribeToDiscussionMutation]
  )

  const createResponse = useCallback(
    (
      responseVariables: CreateDiscussionResponseMutationVariables["response"]
    ) => {
      const optimisticResponse: DiscussionResponseFragment = {
        id: -Math.floor(Math.random() * 1000000),
        parent_id: responseVariables.reply_parent_id || null,
        discussion_id: post?.id!,
        body: responseVariables.body,
        depth: responseVariables.depth || 1,
        attachments: responseVariables.attachments || [],
        likes: 0,
        user_liked: false,
        created_time: new Date().toISOString(),
        user: me,
        archived_time: null,
        __typename: "Response"
      }

      return createResponseMutation({
        variables: {
          context,
          response: {
            ...responseVariables,
            discussion_id: post?.id!
          }
        },
        optimisticResponse: {
          createDiscussionResponse: optimisticResponse
        },

        update: (cache, result) => {
          const newResponse = result.data?.createDiscussionResponse
          if (!newResponse) return

          // Skip eviction/gc on the optimistic pass (same pattern as notes).
          // Evicting + gc while the temp id is still the active result can
          // remove the optimistic row and leave nothing to show until/unless
          // the real result lands cleanly.
          const isOptimistic = newResponse.id === optimisticResponse.id

          const insertResponseEdge = (
            edges: DiscussionResponseEdge[],
            edge: DiscussionResponseEdge
          ) => {
            // Below, we'll insert the new response into list depending on query sort order.
            // Possibly reverse order based on UI ordering (like if response input is at bottom instead of top,
            // for single threaded discussion).

            // First, we'll find the start and end index based on the response parent (the start index
            // is the parent index + 1 and the end index is the child responses last index + 1).
            let startIndex = 0
            let endIndex = edges.length

            // When there is a parent, we need to base start/end indexes on that.
            if (responseVariables.reply_parent_id) {
              const parentIndex = edges.findIndex(
                (e) => e?.node?.id === responseVariables.reply_parent_id
              )
              // We'll insert at least after the parent.
              if (parentIndex !== -1) {
                startIndex = parentIndex + 1

                // Now find the last child response index.
                const lastChildIndex = edges.findLastIndex(
                  (e) =>
                    e?.node?.parent_id === responseVariables.reply_parent_id
                )

                // We'll insert at least after the last child.
                if (lastChildIndex !== -1) {
                  endIndex = lastChildIndex + 1
                } else {
                  endIndex = startIndex
                }
              }
            }

            // Factor in UI sort order, which will reverse the insert indexes.
            if (visuallyReverseResponses) {
              const temp = startIndex
              startIndex = endIndex
              endIndex = temp
            }

            // Now, use the sort order in conjunction with the start/end indexes to find the insert index.
            // I.e., if sorting by Creation Desc, we'll insert at the start index (newest first).
            switch (orderResponsesBy) {
              case ResponseOrderBy.CreationDesc:
              case ResponseOrderBy.ActivityDesc:
              case ResponseOrderBy.Likes:
                // Greatest value first, insert at start index.
                edges.splice(startIndex, 0, edge)
                break
              case ResponseOrderBy.Creation:
              case ResponseOrderBy.Activity:
              case ResponseOrderBy.LikesDesc:
                edges.splice(endIndex, 0, edge)
                break
              default:
                // Fallback to inserting at end.
                edges.push(edge)
            }
          }

          // Runs on both passes. Optimistic: keep the unread indicator off.
          // Success: re-apply after Apollo rolls back the optimistic layer.
          if (post) {
            cache.modify({
              id: cache.identify({
                __typename: "Discussion",
                id: post.id
              }),
              fields: {
                total_responses: (existing: number | undefined) =>
                  Math.max(existing ?? 0, (post.total_responses || 0) + 1)
              }
            })
            writeDiscussionViewedToCache(cache, post, { asResponded: true })
          }

          if (isOptimistic) {
            writeResponsesQuery(cache, (draft) => {
              // Increment discussion responses count and add the new response to the list.
              if (draft.responses && newResponse) {
                const edge: DiscussionResponseEdge = {
                  node: newResponse,
                  __typename: "ResponseEdge"
                }
                const edges = draft?.responses?.edges || []
                draft.responses.total = (draft.responses.total || 0) + 1
                insertResponseEdge(edges, edge)
                draft.responses.edges = [...edges]
              }
            })
            return
          }

          // Real result: replace the optimistic temp entry with the server row.
          cache.evict({ id: cache.identify(optimisticResponse) })

          writeResponsesQuery(cache, (draft) => {
            if (draft.responses && newResponse) {
              const edge: DiscussionResponseEdge = {
                node: newResponse,
                __typename: "ResponseEdge"
              }
              const edges = (draft?.responses?.edges || []).filter(
                // Drop the optimistic edge if eviction left it in the query.
                (e) => e?.node?.id !== optimisticResponse.id
              )

              const existingRealIndex = edges.findIndex(
                (e) => e?.node?.id === newResponse.id
              )
              if (existingRealIndex !== -1) {
                edges[existingRealIndex] = edge
              } else {
                insertResponseEdge(edges, edge)
              }

              draft.responses.edges = [...edges]
            }
          })
        },
        onCompleted: ({ createDiscussionResponse }) => {
          if (createDiscussionResponse) {
            // Persist viewed history on the server. Reinforce cache here too
            // (asResponded) in case a Discussion refetch landed between the
            // success update pass and onCompleted.
            markDiscussionViewed(post, { asResponded: true })

            // Find the response parent if any.
            const responseParent =
              responses.find(
                (response) =>
                  response.id === createDiscussionResponse?.parent_id
              ) || null

            onDiscussionResponseCreated?.(
              post!,
              createDiscussionResponse,
              responseParent
            )
          }
        }
      })
    },
    [
      context,
      post,
      me,
      responses,
      orderResponsesBy,
      visuallyReverseResponses,
      createResponseMutation,
      writeResponsesQuery,
      onDiscussionResponseCreated,
      writeDiscussionViewedToCache,
      markDiscussionViewed
    ]
  )

  const discussionContext = useMemo(
    () => ({
      context,
      id: post?.id || null,
      responseId: responseId || null,
      loading:
        (!post || !responses?.length) &&
        (discussionState.loading ||
          responsesState.loading ||
          createDiscussionState.loading),
      post: post || null,
      postLoading:
        discussionState.loading &&
        !(cachedDiscussion || discussionData?.discussion),
      postError: discussionState.error,
      response: response,
      responseDeleted:
        !responsesState.loading && !filteredResponses.length && !!responseId,
      responses: filteredResponses,
      responsesLoading: responsesState.loading && !responsesData?.responses,
      responsesError: responsesState.error,
      responsesPaginator,
      replyingTo,
      editingResponse,
      deletingResponse,
      setUnreadResponses,
      unreadResponses,
      markAllResponsesAsRead,
      setReplyingTo,
      setEditingResponse,
      setDeletingResponse,
      scrollToResponse,
      scrollRef,
      visuallyReverseResponses,
      viewResponse,
      viewAllResponses,
      orderResponsesBy,
      setOrderResponsesBy: setStoredOrderResponsesBy,
      createDiscussion,
      updateUserDiscussionHistory,
      subscribeToDiscussion,
      unsubscribeToDiscussion,
      createResponse,
      updateResponse,
      deleteResponse,
      likeResponse,
      unlikeResponse
    }),
    [
      context,
      id,
      post,
      responseId,
      discussionState.loading,
      responsesState.loading,
      createDiscussionState.loading,
      cachedDiscussion,
      discussionData?.discussion,
      discussionState.error,
      response,
      responsesState.loading,
      responsesData?.responses,
      responsesState.error,
      responsesPaginator,
      replyingTo,
      editingResponse,
      deletingResponse,
      unreadResponses,
      orderResponsesBy,
      filteredResponses,
      // Callbacks (stable via useCallback)
      markAllResponsesAsRead,
      setReplyingTo,
      setEditingResponse,
      setDeletingResponse,
      scrollToResponse,
      viewResponse,
      viewAllResponses,
      createDiscussion,
      updateUserDiscussionHistory,
      subscribeToDiscussion,
      unsubscribeToDiscussion,
      createResponse,
      updateResponse,
      deleteResponse,
      likeResponse,
      unlikeResponse
    ]
  )

  return (
    <DiscussionContext.Provider value={discussionContext}>
      {children}
    </DiscussionContext.Provider>
  )
}

export default DiscussionProvider
